import { PrimaryButton } from '../../../components/buttons/PrimaryButton';
import { SecondaryButton } from '../../../components/buttons/SecondaryButton';
import FloatingCard from '../../../components/general/FloatingCard';
import { NoticeRow } from '../../../components/general/NoticeRow';
import ConfirmTakeoffDialog from '../../../components/dialogs/ConfirmTakeoffDialog';
import ConfirmTakeoffFromDockDialog from '../../../components/dialogs/ConfirmTakeoffFromDockDialog';
import ConfirmCancelMissionDialog from '../../../components/dialogs/ConfirmCancelMissionDialog';
import ConfirmCancelDockMissionDialog from '../../../components/dialogs/ConfirmCancelDockMissionDialog';
import ConfirmRerouteFromDockDialog from '../../../components/dialogs/ConfirmRerouteFromDockDialog';
import MissionLogsDialog from '../../../components/dialogs/MissionLogsDialog';
import SubmitDiscrepancyReportDialog from '../../../components/dialogs/SubmitDiscrepancyReportDialog';
import VehicleModuleVersionsDialog from '../../../components/dialogs/VehicleModuleVersionsDialog';
import { InfoRow } from '../../../components/general/InfoRow';
import { Text } from '../../../components/general/Text';
import { en } from '../../../locales/en';
import { useDialogQueueStore } from '../../../store/useDialogQueueStore';
import { useGcsStore } from '../../../store/useGcsStore';
import type { VehicleId } from '../../../types/gcs/vehicle';
import type { MenuItemTileProps } from '../../../components/general/MenuItemTile';
import { getUnitFormatters } from '../../../utils/formatters';
import { selectUnitSystem } from '../../../store/selectors/selectUnitSystem';
import VehiclePanelHeader from './VehiclePanelHeader';
import { McErrorTile } from './McErrorTile';
import { MissionStepper } from './MissionStepper';

const noticesCardStyle: React.CSSProperties = {
  border: 'var(--card-border-stroke) var(--card-border-style) #FFF1C2',
  borderRadius: 'var(--card-border-radius)',
  padding: '8px',
  backgroundColor: '#FFFBEB'
};

interface ReadyForTakeoffVehiclePanelProps {
  vehicleId: VehicleId;
  gcsId: string;
  onClose: () => void;
  onFocusVehicle: () => void;
}

function ReadyForTakeoffVehiclePanel({ vehicleId, gcsId, onClose, onFocusVehicle }: ReadyForTakeoffVehiclePanelProps) {
  const { showDialog, hideDialog } = useDialogQueueStore();
  const unitSystem = useGcsStore((state) => selectUnitSystem(state.data, gcsId));
  const unitFormatters = getUnitFormatters(unitSystem);

  const liveMission = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData) return null;
    return gcsData.liveMissionsMap.get(vehicleId);
  });

  const preflightOverview = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData || !liveMission) return undefined;
    return gcsData.missionPreflightOverviewMap.get(liveMission.missionId);
  });

  const takeoffProhibition = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData || !liveMission) return undefined;
    const preflightState = gcsData.missionPreflightStateMap.get(liveMission.missionId);
    return preflightState?.takeoffProhibition;
  });

  const hasDockPreflight = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData || !liveMission) return false;
    return [...gcsData.dockPreflightMissionMap.values()].some(
      (n) => n.dockPreflightInfo?.missionId === liveMission.missionId
    );
  });

  const onCancelMissionClicked = () => {
    if (!liveMission) return;
    showDialog({
      id: `confirm-cancel-mission-${liveMission.missionId}`,
      component: (
        <ConfirmCancelMissionDialog
          gcsId={gcsId}
          vehicleId={vehicleId}
          missionId={liveMission.missionId}
          onClose={hideDialog}
        />
      )
    });
  };

  const onRerouteHomeClicked = () => {
    if (!liveMission) return;
    showDialog({
      id: `confirm-reroute-from-dock-${liveMission.missionId}`,
      component: (
        <ConfirmRerouteFromDockDialog
          gcsId={gcsId}
          vehicleId={vehicleId}
          missionId={liveMission.missionId}
          onClose={hideDialog}
        />
      )
    });
  };

  const onTakeoffClicked = () => {
    if (!liveMission) return;
    showDialog({
      id: `approve-takeoff-${vehicleId}`,
      component: hasDockPreflight ? (
        <ConfirmTakeoffFromDockDialog
          gcsId={gcsId}
          vehicleId={vehicleId}
          missionId={liveMission.missionId}
          onClose={hideDialog}
        />
      ) : (
        <ConfirmTakeoffDialog
          gcsId={gcsId}
          vehicleId={vehicleId}
          missionId={liveMission.missionId}
          onClose={hideDialog}
        />
      )
    });
  };

  const menuItems: MenuItemTileProps[] = [
    {
      label: en.vehiclePanelMenuItems.showVersions,
      onClick: () => {
        showDialog({
          id: `vehicle-module-versions-${vehicleId}`,
          component: <VehicleModuleVersionsDialog vehicleId={vehicleId} onClose={hideDialog} />
        });
      }
    },
    {
      label: en.vehiclePanelMenuItems.missionLogs,
      onClick: () => {
        if (!liveMission) return;
        showDialog({
          id: `mission-logs-dialog-${liveMission.missionId}`,
          component: (
            <MissionLogsDialog
              gcsId={gcsId}
              vehicleId={vehicleId}
              missionId={liveMission.missionId}
              onClose={hideDialog}
            />
          )
        });
      }
    },
    ...(!hasDockPreflight
      ? [
          {
            label: en.vehiclePanelMenuItems.discrepancyReport,
            onClick: () => {
              showDialog({
                id: 'submit-discrepancy-report',
                component: (
                  <SubmitDiscrepancyReportDialog
                    gcsId={gcsId}
                    vehicleId={vehicleId}
                    onClose={() => useDialogQueueStore.getState().hideDialog()}
                  />
                )
              });
            }
          }
        ]
      : liveMission
        ? [
            {
              label: en.vehiclePanelMenuItems.cancelMission,
              onClick: () => {
                showDialog({
                  id: `confirm-cancel-dock-mission-${liveMission.missionId}`,
                  component: (
                    <ConfirmCancelDockMissionDialog
                      gcsId={gcsId}
                      vehicleId={vehicleId}
                      missionId={liveMission.missionId}
                      onClose={hideDialog}
                    />
                  )
                });
              }
            }
          ]
        : [])
  ];

  return (
    <FloatingCard>
      <VehiclePanelHeader
        vehicleId={vehicleId}
        gcsId={gcsId}
        onFocusVehicle={onFocusVehicle}
        onClose={onClose}
        menuItems={menuItems}
      />
      <McErrorTile gcsId={gcsId} vehicleId={vehicleId} />

      {/* Scrollable Content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          flex: 1,
          overflowY: 'auto'
        }}
      >
        <MissionStepper gcsId={gcsId} vehicleId={vehicleId} />
        {/* Notices Card */}
        {liveMission &&
          (liveMission.package?.order?.isFirstToDeliveryPoint ||
            liveMission.package?.order?.isPriority ||
            liveMission.package?.isRefly) && (
            <div style={noticesCardStyle}>
              {liveMission.package?.order?.isFirstToDeliveryPoint && (
                <NoticeRow text={en.preflightPanel.firstToDeliveryPoint} />
              )}
              {liveMission.package?.order?.isPriority && <NoticeRow text={en.preflightPanel.jetDelivery} />}
              {liveMission.package?.isRefly && <NoticeRow text={en.preflightPanel.refly} />}
            </div>
          )}

        {/* Mission Info Card */}
        {liveMission && !hasDockPreflight && (
          <div
            style={{
              border: 'var(--card-border-stroke) var(--card-border-style) var(--card-border-color)',
              borderRadius: 'var(--card-border-radius)',
              padding: '16px',
              backgroundColor: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}
          >
            <InfoRow
              label={en.preflightPanel.distance}
              value={
                preflightOverview
                  ? unitFormatters.formatDistance(preflightOverview.batteryConsumptionEstimation.flightDistanceM)
                  : '-'
              }
            />
          </div>
        )}

        {/* Order/Package Info Card */}
        {liveMission && (
          <div
            style={{
              border: 'var(--card-border-stroke) var(--card-border-style) var(--card-border-color)',
              borderRadius: 'var(--card-border-radius)',
              padding: '16px',
              backgroundColor: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}
          >
            <InfoRow label={en.preflightPanel.order} value={liveMission.package?.order?.name ?? '-'} />
            <InfoRow
              label={en.preflightPanel.packageWeight}
              value={
                liveMission.package?.weightKg != null ? unitFormatters.formatWeight(liveMission.package.weightKg) : '-'
              }
            />
          </div>
        )}
      </div>

      {/* Button Container */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        {/* Takeoff Prohibition Warning */}
        {takeoffProhibition && (
          <Text
            as='span'
            variant='body2'
            style={{ color: 'var(--color-error)', display: 'block', textAlign: 'center' }}
          >
            {takeoffProhibition}
          </Text>
        )}

        {/* Buttons Row */}
        <div
          style={{
            display: 'flex',
            gap: '16px'
          }}
        >
          {/* Cancel Mission / Reroute Home Button */}
          <div style={{ flex: 1 }}>
            {hasDockPreflight ? (
              <SecondaryButton text={en.preflightPanel.rerouteHomeButton} callback={onRerouteHomeClicked} />
            ) : (
              <SecondaryButton text={en.preflightPanel.cancelMissionButton} callback={onCancelMissionClicked} />
            )}
          </div>

          {/* Takeoff Button */}
          <div style={{ flex: 1 }}>
            <PrimaryButton
              text={en.preflightPanel.takeoffButton}
              callback={onTakeoffClicked}
              backgroundColor={'var(--color-success)'}
              foregroundColor={'#ffffff'}
              isEnabled={!takeoffProhibition}
            />
          </div>
        </div>
      </div>
    </FloatingCard>
  );
}

export default ReadyForTakeoffVehiclePanel;
